<template>
    <v-container fluid class="pa-0">
        <v-card>
            <v-card-title primary-title>
                <v-row>
                    <v-col>Orders</v-col>
                    <v-spacer />
                    <v-col class="text-right">
                        <v-btn @click="openOrderDialog('create', null)">Create New Order</v-btn>
                    </v-col>
                </v-row>
            </v-card-title>
            <v-card-text>
                <orders-list
                    :orders="orders"
                    :folders="folderOptions"
                    :search="search"
                    @orderUpdated="reloadOrders"
                    @editOrder="handleEditOrder"
                    @orderDeleted="reloadOrders"
                    @reportOrderItemFailure="openReportFailureDialog"
                />
            </v-card-text>
        </v-card>
        <v-dialog
            v-model="isOrderDialogVisible"
            persistent
            max-width="600px"
            @click:outside="closeOrderDialog"
            @keyup.esc="closeOrderDialog"
        >
            <order-form
                :benchlingFolderOptions="folderOptions"
                :mode="orderDialogMode"
                :order="selectedOrder"
                @closeForm="closeOrderDialog"
                @orderSaved="handleOrderSaved"
                @orderError="handleOrderError"
            />
        </v-dialog>
        <v-dialog
            v-model="isReportItemFailureDialogVisible"
            persistent
            max-width="600px"
            @click:outside="closeReportFailureDialog"
            @keyup.esc="closeReportFailureDialog"
        >
            <order-item-failure-form
                :order="selectedOrder!"
                @closeForm="closeReportFailureDialog"
            />
        </v-dialog>
    </v-container>
</template>

<script setup lang="ts">
import OrdersList from "@/components/Orders/OrdersList.vue";
import { withLoadingScreen } from "@/lib/loading.js";
import { ordersAPI, SavedOrder } from "@/services/orders/ordersAPI";
import { ref, onMounted } from "vue";
import OrderForm from "./OrderForm.vue";
import { useToast } from "vue-toastification";
import { FolderOption } from "@/services/commonModels";
import OrderItemFailureForm from "./OrderItemFailureForm.vue";

const toast = useToast();
const orders = ref<SavedOrder[]>([]);
const folderOptions = ref<FolderOption[]>([]);
const isOrderDialogVisible = ref(false);
const orderDialogMode = ref("");
const selectedOrder = ref<SavedOrder | null>(null);
const isReportItemFailureDialogVisible = ref(false);

interface Props {
    search: string | undefined;
}

withDefaults(defineProps<Props>(), {
    search: "",
});

onMounted(async () => {
    reloadOrders();
});

async function reloadOrders() {
    await withLoadingScreen(async () => {
        folderOptions.value = await ordersAPI.fetchOrderFolders();
        orders.value = await ordersAPI.fetchAllOrders();
    });
}

function openOrderDialog(mode: string, item: SavedOrder | null) {
    orderDialogMode.value = mode;
    selectedOrder.value = item;
    isOrderDialogVisible.value = true;
}

function handleEditOrder(item: SavedOrder) {
    selectedOrder.value = item;
    openOrderDialog("edit", item);
}

function closeOrderDialog() {
    isOrderDialogVisible.value = false;
    selectedOrder.value = null;
}

function openReportFailureDialog(item: SavedOrder) {
    selectedOrder.value = item;
    isReportItemFailureDialogVisible.value = true;
}

function closeReportFailureDialog() {
    isReportItemFailureDialogVisible.value = false;
}

function handleOrderSaved(saveResult) {
    const keptPeptidesMessage =
        saveResult.keptPeptideCodes && saveResult.keptPeptideCodes.length > 0
            ? `${saveResult.keptPeptideCodes.join(", ")} not removed because data has already been entered.`
            : "";
    toast.success(`Order ${saveResult.order.orderCode} saved. ${keptPeptidesMessage}`);
    closeOrderDialog();
    reloadOrders();
}

function handleOrderError(error: string) {
    toast.error(error);
    closeOrderDialog();
}

</script>
